// ============================================
// MEDFLOW - Supabase Data Queries
// Shared read models for the v2 multi-tenant frontend
// ============================================

import { addMinutes, eachDayOfInterval, format, parseISO } from 'date-fns';
import { supabase } from '@/lib/supabase';

const palette = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

function getSupabaseClient() {
  if (!supabase) {
    throw new Error('Supabase no está configurado todavía.');
  }

  return supabase;
}

function requireClinicId(clinicId?: string | null) {
  if (!clinicId) {
    throw new Error('No hay una clínica activa seleccionada.');
  }

  return clinicId;
}

function toMap<T extends { id: string }>(items: T[]) {
  return new Map(items.map((item) => [item.id, item]));
}

function unique(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value))));
}

function getColorFromSeed(seed: string) {
  const hash = Array.from(seed).reduce((total, char) => total + char.charCodeAt(0), 0);
  return palette[hash % palette.length];
}

async function fetchUserProfiles(userIds: string[]) {
  if (userIds.length === 0) {
    return new Map<string, { id: string; fullName: string; email: string; phone?: string }>();
  }

  const client = getSupabaseClient();
  const { data, error } = await client
    .from('user_profiles')
    .select('id, full_name, email, phone')
    .in('id', userIds);

  if (error) {
    throw new Error(error.message || 'No se pudieron cargar los perfiles de usuario.');
  }

  const profiles = ((data ?? []) as Array<Record<string, unknown>>).map((profile) => ({
    id: String(profile.id),
    fullName: String(profile.full_name ?? 'Profesional'),
    email: String(profile.email ?? ''),
    phone: profile.phone ? String(profile.phone) : undefined,
  }));

  return toMap(profiles);
}

async function fetchProfessionalsBase(clinicId: string, activeOnly = true) {
  const client = getSupabaseClient();
  let query = client
    .from('professionals')
    .select('id, user_id, specialty, color, is_active')
    .eq('clinic_id', clinicId);

  if (activeOnly) {
    query = query.eq('is_active', true);
  }

  const { data, error } = await query.order('specialty', { ascending: true });

  if (error) {
    throw new Error(error.message || 'No se pudieron cargar los profesionales.');
  }

  return ((data ?? []) as Array<Record<string, unknown>>).map((professional) => ({
    id: String(professional.id),
    userId: String(professional.user_id),
    specialty: String(professional.specialty ?? ''),
    color: String(professional.color ?? '#6366f1'),
    isActive: Boolean(professional.is_active),
  }));
}

async function fetchServicesBase(clinicId: string, activeOnly = false) {
  const client = getSupabaseClient();
  let query = client
    .from('services')
    .select('id, name, description, duration_minutes, price, is_active')
    .eq('clinic_id', clinicId);

  if (activeOnly) {
    query = query.eq('is_active', true);
  }

  const { data, error } = await query.order('name', { ascending: true });

  if (error) {
    throw new Error(error.message || 'No se pudieron cargar los servicios.');
  }

  return ((data ?? []) as Array<Record<string, unknown>>).map((service) => ({
    id: String(service.id),
    name: String(service.name ?? 'Servicio'),
    description: service.description ? String(service.description) : undefined,
    durationMinutes: Number(service.duration_minutes ?? 0),
    price: Number(service.price ?? 0),
    isActive: Boolean(service.is_active),
  }));
}

async function fetchProfessionalServiceLinks({ professionalIds, serviceIds }: { professionalIds?: string[]; serviceIds?: string[] }) {
  const client = getSupabaseClient();
  let query = client
    .from('professional_services')
    .select('professional_id, service_id');

  if (professionalIds && professionalIds.length > 0) {
    query = query.in('professional_id', professionalIds);
  }

  if (serviceIds && serviceIds.length > 0) {
    query = query.in('service_id', serviceIds);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message || 'No se pudieron cargar las relaciones profesional-servicio.');
  }

  return ((data ?? []) as Array<Record<string, unknown>>).map((link) => ({
    professionalId: String(link.professional_id),
    serviceId: String(link.service_id),
  }));
}

export async function loadSupabaseBoxes(clinicIdInput: string, activeOnly = false) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  let query = client
    .from('boxes')
    .select('id, name, description, is_active')
    .eq('clinic_id', clinicId)
    .order('name', { ascending: true });

  if (activeOnly) {
    query = query.eq('is_active', true);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message || 'No se pudieron cargar los boxes.');
  }

  return ((data ?? []) as Array<Record<string, unknown>>).map((box) => ({
    id: String(box.id),
    name: String(box.name ?? 'Box'),
    description: box.description ? String(box.description) : null,
    isActive: Boolean(box.is_active),
  }));
}

export async function loadSupabaseBoxesWithSchedules(
  clinicIdInput: string,
  status: 'active' | 'inactive' | 'all' = 'active'
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  let query = client
    .from('boxes')
    .select('id, name, description, is_active')
    .eq('clinic_id', clinicId)
    .order('name', { ascending: true });

  if (status === 'active') {
    query = query.eq('is_active', true);
  }

  if (status === 'inactive') {
    query = query.eq('is_active', false);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message || 'No se pudieron cargar los boxes.');
  }

  const boxes = ((data ?? []) as Array<Record<string, unknown>>).map((box) => ({
    id: String(box.id),
    name: String(box.name ?? 'Box'),
    description: box.description ? String(box.description) : null,
    isActive: Boolean(box.is_active),
  }));

  const boxIds = boxes.map((box) => box.id);

  if (boxIds.length === 0) {
    return boxes.map((box) => ({ ...box, schedules: [] as Array<{
      id: string;
      dayOfWeek: number;
      startTime: string | null;
      endTime: string | null;
      startTime2: string | null;
      endTime2: string | null;
      isOpen: boolean;
    }> }));
  }

  const { data: scheduleData, error: scheduleError } = await client
    .from('box_schedules')
    .select('id, box_id, day_of_week, start_time, end_time, start_time_2, end_time_2, is_open')
    .in('box_id', boxIds)
    .order('day_of_week', { ascending: true });

  if (scheduleError) {
    throw new Error(scheduleError.message || 'No se pudieron cargar los horarios de boxes.');
  }

  const schedulesByBox = new Map<string, Array<{
    id: string;
    dayOfWeek: number;
    startTime: string | null;
    endTime: string | null;
    startTime2: string | null;
    endTime2: string | null;
    isOpen: boolean;
  }>>();

  for (const schedule of (scheduleData ?? []) as Array<Record<string, unknown>>) {
    const boxId = String(schedule.box_id);
    const currentSchedules = schedulesByBox.get(boxId) ?? [];
    currentSchedules.push({
      id: String(schedule.id),
      dayOfWeek: Number(schedule.day_of_week ?? 0),
      startTime: schedule.start_time ? String(schedule.start_time) : null,
      endTime: schedule.end_time ? String(schedule.end_time) : null,
      startTime2: schedule.start_time_2 ? String(schedule.start_time_2) : null,
      endTime2: schedule.end_time_2 ? String(schedule.end_time_2) : null,
      isOpen: Boolean(schedule.is_open),
    });
    schedulesByBox.set(boxId, currentSchedules);
  }

  return boxes.map((box) => ({
    ...box,
    schedules: schedulesByBox.get(box.id) ?? [],
  }));
}

export async function createSupabaseBox(clinicIdInput: string, data: { name: string; description?: string }) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client
    .from('boxes')
    .insert({
      clinic_id: clinicId,
      name: data.name,
      description: data.description ?? null,
      is_active: true,
    });

  if (error) {
    throw new Error(error.message || 'No se pudo crear el box.');
  }
}

export async function updateSupabaseBox(boxId: string, data: { name?: string; description?: string; isActive?: boolean }) {
  const client = getSupabaseClient();
  const payload: Record<string, unknown> = {};

  if (typeof data.name !== 'undefined') {
    payload.name = data.name;
  }

  if (typeof data.description !== 'undefined') {
    payload.description = data.description ?? null;
  }

  if (typeof data.isActive !== 'undefined') {
    payload.is_active = data.isActive;
  }

  const { error } = await client
    .from('boxes')
    .update(payload)
    .eq('id', boxId);

  if (error) {
    throw new Error(error.message || 'No se pudo actualizar el box.');
  }
}

export async function updateSupabaseBoxSchedule(
  boxId: string,
  schedule: Array<{
    dayOfWeek: number;
    startTime?: string;
    endTime?: string;
    startTime2?: string;
    endTime2?: string;
    isOpen: boolean;
  }>
) {
  const client = getSupabaseClient();
  const { error: deleteError } = await client
    .from('box_schedules')
    .delete()
    .eq('box_id', boxId);

  if (deleteError) {
    throw new Error(deleteError.message || 'No se pudieron limpiar los horarios actuales del box.');
  }

  const payload = schedule.map((item) => ({
    box_id: boxId,
    day_of_week: item.dayOfWeek,
    start_time: item.isOpen ? item.startTime ?? null : null,
    end_time: item.isOpen ? item.endTime ?? null : null,
    start_time_2: item.isOpen ? item.startTime2 ?? null : null,
    end_time_2: item.isOpen ? item.endTime2 ?? null : null,
    is_open: item.isOpen,
  }));

  if (payload.length === 0) {
    return;
  }

  const { error: insertError } = await client
    .from('box_schedules')
    .insert(payload);

  if (insertError) {
    throw new Error(insertError.message || 'No se pudieron guardar los horarios del box.');
  }
}

export async function loadSupabaseProfessionals(clinicIdInput: string, activeOnly = true) {
  const clinicId = requireClinicId(clinicIdInput);
  const professionals = await fetchProfessionalsBase(clinicId, activeOnly);
  const userProfiles = await fetchUserProfiles(unique(professionals.map((professional) => professional.userId)));
  const links = await fetchProfessionalServiceLinks({ professionalIds: professionals.map((professional) => professional.id) });
  const services = await fetchServicesBase(clinicId, false);
  const servicesMap = toMap(services);

  return professionals
    .map((professional) => {
      const profile = userProfiles.get(professional.userId);
      const professionalServices = links
        .filter((link) => link.professionalId === professional.id)
        .map((link) => servicesMap.get(link.serviceId))
        .filter((service): service is NonNullable<typeof service> => Boolean(service));

      return {
        id: professional.id,
        color: professional.color,
        specialty: professional.specialty,
        isActive: professional.isActive,
        user: {
          name: profile?.fullName ?? 'Profesional',
          email: profile?.email ?? '',
          phone: profile?.phone,
        },
        services: professionalServices.map((service) => ({
          id: service.id,
          name: service.name,
          durationMinutes: service.durationMinutes,
        })),
        schedule: {} as Record<string, unknown[]>,
      };
    })
    .sort((left, right) => left.user.name.localeCompare(right.user.name, 'es'));
}

export async function provisionSupabaseProfessional(
  clinicIdInput: string,
  data: {
    email: string;
    fullName: string;
    phone?: string;
    specialty: string;
    color?: string;
    serviceIds?: string[];
    temporaryPassword?: string;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { data: response, error } = await client.functions.invoke('manage-professional', {
    body: {
      action: 'create',
      clinicId,
      ...data,
    },
  });

  if (error) {
    throw new Error(error.message || 'No se pudo provisionar el profesional.');
  }

  if (!response?.success) {
    throw new Error(response?.error || 'No se pudo provisionar el profesional.');
  }

  return response.data as {
    professionalId: string;
    userId: string;
    email: string;
    temporaryPassword: string | null;
    createdNewUser: boolean;
  };
}

export async function updateSupabaseProfessional(
  clinicIdInput: string,
  data: {
    professionalId: string;
    fullName?: string;
    phone?: string;
    specialty?: string;
    color?: string;
    isActive?: boolean;
    serviceIds?: string[];
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { data: response, error } = await client.functions.invoke('manage-professional', {
    body: {
      action: 'update',
      clinicId,
      ...data,
    },
  });

  if (error) {
    throw new Error(error.message || 'No se pudo actualizar el profesional.');
  }

  if (!response?.success) {
    throw new Error(response?.error || 'No se pudo actualizar el profesional.');
  }

  return response.data as { professionalId: string; userId: string };
}

export async function loadSupabaseProfessionalAgents(clinicIdInput: string) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('professional_agents')
    .select('id, professional_id, name, channel, is_active, model, system_prompt, configuration')
    .eq('clinic_id', clinicId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message || 'No se pudieron cargar las configuraciones de agente.');
  }

  return ((data ?? []) as Array<Record<string, unknown>>).map((agent) => ({
    id: String(agent.id),
    professionalId: String(agent.professional_id),
    name: String(agent.name ?? 'Agente'),
    channel: String(agent.channel ?? 'whatsapp'),
    isActive: Boolean(agent.is_active),
    model: agent.model ? String(agent.model) : '',
    systemPrompt: agent.system_prompt ? String(agent.system_prompt) : '',
    configuration: (agent.configuration as Record<string, unknown> | null) ?? {},
  }));
}

export async function upsertSupabaseProfessionalAgent(
  clinicIdInput: string,
  data: {
    professionalId: string;
    channel: 'whatsapp' | 'webchat' | 'instagram' | 'telegram';
    name: string;
    isActive: boolean;
    model?: string;
    systemPrompt?: string;
    configuration?: Record<string, unknown>;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client.from('professional_agents').upsert(
    {
      clinic_id: clinicId,
      professional_id: data.professionalId,
      name: data.name.trim(),
      channel: data.channel,
      is_active: data.isActive,
      model: data.model?.trim() || null,
      system_prompt: data.systemPrompt?.trim() || null,
      configuration: data.configuration ?? {},
    },
    { onConflict: 'professional_id,channel' }
  );

  if (error) {
    throw new Error(error.message || 'No se pudo guardar la configuración del agente.');
  }
}

export async function loadSupabaseServices(clinicIdInput: string) {
  const clinicId = requireClinicId(clinicIdInput);
  const services = await fetchServicesBase(clinicId, false);
  const professionals = await fetchProfessionalsBase(clinicId, false);
  const userProfiles = await fetchUserProfiles(unique(professionals.map((professional) => professional.userId)));
  const professionalMap = new Map(
    professionals.map((professional) => {
      const profile = userProfiles.get(professional.userId);
      return [
        professional.id,
        {
          id: professional.id,
          specialty: professional.specialty,
          color: professional.color,
          user: {
            name: profile?.fullName ?? 'Profesional',
            email: profile?.email ?? '',
            phone: profile?.phone,
          },
        },
      ] as const;
    })
  );
  const links = await fetchProfessionalServiceLinks({ serviceIds: services.map((service) => service.id) });

  return services.map((service) => ({
    id: service.id,
    name: service.name,
    description: service.description,
    durationMinutes: service.durationMinutes,
    price: service.price,
    color: getColorFromSeed(service.name),
    isActive: service.isActive,
    professionals: links
      .filter((link) => link.serviceId === service.id)
      .map((link) => professionalMap.get(link.professionalId))
      .filter((professional): professional is NonNullable<typeof professional> => Boolean(professional)),
  }));
}

export async function createSupabaseService(
  clinicIdInput: string,
  data: { name: string; description?: string; durationMinutes: number; price?: number }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client
    .from('services')
    .insert({
      clinic_id: clinicId,
      name: data.name,
      description: data.description?.trim() || null,
      duration_minutes: data.durationMinutes,
      price: data.price ?? 0,
      is_active: true,
    });

  if (error) {
    throw new Error(error.message || 'No se pudo crear el servicio.');
  }
}

export async function updateSupabaseService(
  serviceId: string,
  data: Partial<{ name: string; description?: string; durationMinutes: number; price?: number; isActive: boolean }>
) {
  const client = getSupabaseClient();
  const payload: Record<string, unknown> = {};

  if (typeof data.name !== 'undefined') {
    payload.name = data.name;
  }

  if (typeof data.description !== 'undefined') {
    payload.description = data.description?.trim() || null;
  }

  if (typeof data.durationMinutes !== 'undefined') {
    payload.duration_minutes = data.durationMinutes;
  }

  if (typeof data.price !== 'undefined') {
    payload.price = data.price;
  }

  if (typeof data.isActive !== 'undefined') {
    payload.is_active = data.isActive;
  }

  const { error } = await client
    .from('services')
    .update(payload)
    .eq('id', serviceId);

  if (error) {
    throw new Error(error.message || 'No se pudo actualizar el servicio.');
  }
}

export async function loadSupabaseAppointments(clinicIdInput: string, params: { startDate: string; endDate: string; professionalId?: string }) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  let query = client
    .from('appointments')
    .select('id, start_time, end_time, status, patient_id, professional_id, service_id, box_id')
    .eq('clinic_id', clinicId)
    .gte('start_time', `${params.startDate}T00:00:00.000Z`)
    .lte('start_time', `${params.endDate}T23:59:59.999Z`)
    .order('start_time', { ascending: true });

  if (params.professionalId) {
    query = query.eq('professional_id', params.professionalId);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message || 'No se pudieron cargar las citas.');
  }

  const appointments = ((data ?? []) as Array<Record<string, unknown>>).map((appointment) => ({
    id: String(appointment.id),
    startTime: String(appointment.start_time),
    endTime: String(appointment.end_time),
    status: String(appointment.status ?? 'pending'),
    patientId: String(appointment.patient_id),
    professionalId: String(appointment.professional_id),
    serviceId: String(appointment.service_id),
    boxId: appointment.box_id ? String(appointment.box_id) : null,
  }));

  const patientIds = unique(appointments.map((appointment) => appointment.patientId));
  const serviceIds = unique(appointments.map((appointment) => appointment.serviceId));
  const professionalIds = unique(appointments.map((appointment) => appointment.professionalId));
  const boxIds = unique(appointments.map((appointment) => appointment.boxId));

  const [patientsResult, servicesResult, professionalsBase, boxesResult] = await Promise.all([
    patientIds.length === 0
      ? Promise.resolve([] as Array<{ id: string; fullName: string }>)
      : client.from('patients').select('id, full_name').in('id', patientIds).then(({ data: patientData, error: patientError }) => {
          if (patientError) {
            throw new Error(patientError.message || 'No se pudieron cargar los pacientes.');
          }

          return ((patientData ?? []) as Array<Record<string, unknown>>).map((patient) => ({
            id: String(patient.id),
            fullName: String(patient.full_name ?? 'Paciente'),
          }));
        }),
    serviceIds.length === 0
      ? Promise.resolve([] as Array<{ id: string; name: string }>)
      : client.from('services').select('id, name').in('id', serviceIds).then(({ data: serviceData, error: serviceError }) => {
          if (serviceError) {
            throw new Error(serviceError.message || 'No se pudieron cargar los servicios.');
          }

          return ((serviceData ?? []) as Array<Record<string, unknown>>).map((service) => ({
            id: String(service.id),
            name: String(service.name ?? 'Servicio'),
          }));
        }),
    professionalIds.length === 0
      ? Promise.resolve([] as Array<{ id: string; userId: string; color: string; specialty: string }>)
      : client.from('professionals').select('id, user_id, color, specialty').in('id', professionalIds).then(({ data: professionalData, error: professionalError }) => {
          if (professionalError) {
            throw new Error(professionalError.message || 'No se pudieron cargar los profesionales.');
          }

          return ((professionalData ?? []) as Array<Record<string, unknown>>).map((professional) => ({
            id: String(professional.id),
            userId: String(professional.user_id),
            color: String(professional.color ?? '#6366f1'),
            specialty: String(professional.specialty ?? ''),
          }));
        }),
    boxIds.length === 0
      ? Promise.resolve([] as Array<{ id: string; name: string }>)
      : client.from('boxes').select('id, name').in('id', boxIds).then(({ data: boxData, error: boxError }) => {
          if (boxError) {
            throw new Error(boxError.message || 'No se pudieron cargar los boxes.');
          }

          return ((boxData ?? []) as Array<Record<string, unknown>>).map((box) => ({
            id: String(box.id),
            name: String(box.name ?? 'Box'),
          }));
        }),
  ]);

  const professionalProfiles = await fetchUserProfiles(unique(professionalsBase.map((professional) => professional.userId)));
  const patientsMap = toMap(patientsResult.map((patient) => ({ id: patient.id, name: patient.fullName })));
  const servicesMap = toMap(servicesResult);
  const boxesMap = toMap(boxesResult);
  const professionalsMap = new Map(
    professionalsBase.map((professional) => [
      professional.id,
      {
        color: professional.color,
        specialty: professional.specialty,
        user: {
          name: professionalProfiles.get(professional.userId)?.fullName ?? 'Profesional',
        },
      },
    ])
  );

  return appointments.map((appointment) => ({
    id: appointment.id,
    startTime: appointment.startTime,
    endTime: appointment.endTime,
    status: appointment.status,
    patient: { name: patientsMap.get(appointment.patientId)?.name ?? 'Paciente' },
    professional: professionalsMap.get(appointment.professionalId),
    service: { name: servicesMap.get(appointment.serviceId)?.name ?? 'Servicio' },
    box: appointment.boxId ? boxesMap.get(appointment.boxId) ?? null : null,
  }));
}

export async function loadSupabasePatientsList(clinicIdInput: string) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('patients')
    .select('id, full_name, phone, email')
    .eq('clinic_id', clinicId)
    .order('full_name', { ascending: true });

  if (error) {
    throw new Error(error.message || 'No se pudieron cargar los pacientes.');
  }

  return ((data ?? []) as Array<Record<string, unknown>>).map((patient) => ({
    id: String(patient.id),
    name: String(patient.full_name ?? 'Paciente'),
    phone: patient.phone ? String(patient.phone) : '',
    email: patient.email ? String(patient.email) : undefined,
  }));
}

export async function loadSupabaseClinicalRecord(clinicIdInput: string, patientId: string) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { data: recordData, error: recordError } = await client
    .from('clinical_records')
    .select('id, summary, current_diagnosis, allergies, chronic_conditions, metadata, created_at, updated_at')
    .eq('clinic_id', clinicId)
    .eq('patient_id', patientId)
    .maybeSingle();

  if (recordError) {
    throw new Error(recordError.message || 'No se pudo cargar la ficha clínica.');
  }

  if (!recordData) {
    return {
      record: null,
      notes: [] as Array<{
        id: string;
        title: string;
        noteType: string;
        text: string;
        createdAt: string;
      }>,
    };
  }

  const { data: notesData, error: notesError } = await client
    .from('clinical_notes')
    .select('id, title, note_type, content, created_at')
    .eq('clinic_id', clinicId)
    .eq('patient_id', patientId)
    .order('created_at', { ascending: false });

  if (notesError) {
    throw new Error(notesError.message || 'No se pudieron cargar las notas clínicas.');
  }

  return {
    record: {
      id: String(recordData.id),
      summary: String(recordData.summary ?? ''),
      currentDiagnosis: String(recordData.current_diagnosis ?? ''),
      allergies: String(recordData.allergies ?? ''),
      chronicConditions: String(recordData.chronic_conditions ?? ''),
      metadata: (recordData.metadata as Record<string, unknown> | null) ?? {},
      createdAt: String(recordData.created_at ?? new Date().toISOString()),
      updatedAt: String(recordData.updated_at ?? new Date().toISOString()),
    },
    notes: ((notesData ?? []) as Array<Record<string, unknown>>).map((note) => ({
      id: String(note.id),
      title: String(note.title ?? 'Nota clínica'),
      noteType: String(note.note_type ?? 'progress'),
      text: String(((note.content as Record<string, unknown> | null)?.text as string | undefined) ?? ''),
      createdAt: String(note.created_at ?? new Date().toISOString()),
    })),
  };
}

export async function saveSupabaseClinicalRecord(
  clinicIdInput: string,
  patientId: string,
  data: {
    summary: string;
    currentDiagnosis: string;
    allergies: string;
    chronicConditions: string;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { data: existing, error: existingError } = await client
    .from('clinical_records')
    .select('id')
    .eq('clinic_id', clinicId)
    .eq('patient_id', patientId)
    .maybeSingle();

  if (existingError) {
    throw new Error(existingError.message || 'No se pudo preparar la ficha clínica.');
  }

  const payload = {
    clinic_id: clinicId,
    patient_id: patientId,
    summary: data.summary.trim() || null,
    current_diagnosis: data.currentDiagnosis.trim() || null,
    allergies: data.allergies.trim() || null,
    chronic_conditions: data.chronicConditions.trim() || null,
  };

  if (existing?.id) {
    const { error } = await client.from('clinical_records').update(payload).eq('id', existing.id);
    if (error) {
      throw new Error(error.message || 'No se pudo actualizar la ficha clínica.');
    }
    return String(existing.id);
  }

  const { data: inserted, error } = await client
    .from('clinical_records')
    .insert(payload)
    .select('id')
    .single();

  if (error || !inserted) {
    throw new Error(error?.message || 'No se pudo crear la ficha clínica.');
  }

  return String(inserted.id);
}

export async function addSupabaseClinicalNote(
  clinicIdInput: string,
  patientId: string,
  recordId: string,
  data: { title: string; noteType?: string; text: string; authorUserId?: string | null }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client
    .from('clinical_notes')
    .insert({
      clinic_id: clinicId,
      patient_id: patientId,
      record_id: recordId,
      author_user_id: data.authorUserId ?? null,
      note_type: data.noteType ?? 'progress',
      title: data.title.trim() || 'Nota clínica',
      content: { text: data.text.trim() },
    });

  if (error) {
    throw new Error(error.message || 'No se pudo guardar la nota clínica.');
  }
}

function sanitizeStorageName(filename: string) {
  const normalized = filename
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalized || 'archivo';
}

export async function createSupabaseFileAssetSignedUrl(objectPath: string, bucketId = 'medflow-clinical-files') {
  const client = getSupabaseClient();
  const { data, error } = await client.storage.from(bucketId).createSignedUrl(objectPath, 60 * 30);

  if (error || !data?.signedUrl) {
    throw new Error(error?.message || 'No se pudo generar el enlace del archivo.');
  }

  return data.signedUrl;
}

export async function uploadSupabaseFileAsset(
  clinicIdInput: string,
  patientId: string,
  file: File,
  data?: {
    recordId?: string | null;
    appointmentId?: string | null;
    uploadedByUserId?: string | null;
    category?: 'image' | 'pdf' | 'document' | 'audio' | 'video' | 'consent' | 'lab' | 'other';
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const bucketId = 'medflow-clinical-files';
  const safeName = sanitizeStorageName(file.name);
  const objectPath = `${clinicId}/${patientId}/${Date.now()}-${safeName}`;

  const { error: uploadError } = await client.storage.from(bucketId).upload(objectPath, file, {
    contentType: file.type || undefined,
    upsert: false,
  });

  if (uploadError) {
    throw new Error(uploadError.message || 'No se pudo subir el archivo.');
  }

  const { data: inserted, error } = await client
    .from('file_assets')
    .insert({
      clinic_id: clinicId,
      patient_id: patientId,
      record_id: data?.recordId ?? null,
      appointment_id: data?.appointmentId ?? null,
      uploaded_by_user_id: data?.uploadedByUserId ?? null,
      bucket_id: bucketId,
      object_path: objectPath,
      original_filename: file.name,
      mime_type: file.type || null,
      file_size_bytes: file.size,
      category: data?.category ?? 'document',
    })
    .select('id, bucket_id, object_path, original_filename, mime_type, file_size_bytes, category, created_at')
    .single();

  if (error || !inserted) {
    throw new Error(error?.message || 'No se pudo registrar el archivo clínico.');
  }

  return {
    id: String(inserted.id),
    bucketId: String(inserted.bucket_id),
    objectPath: String(inserted.object_path),
    originalFilename: String(inserted.original_filename),
    mimeType: inserted.mime_type ? String(inserted.mime_type) : null,
    fileSizeBytes: Number(inserted.file_size_bytes ?? 0),
    category: String(inserted.category ?? 'document'),
    createdAt: String(inserted.created_at ?? new Date().toISOString()),
  };
}

export async function createSupabasePatientConsent(
  clinicIdInput: string,
  patientId: string,
  data: {
    title: string;
    version?: string;
    status?: 'draft' | 'sent' | 'accepted' | 'rejected' | 'revoked';
    recordId?: string | null;
    appointmentId?: string | null;
    templateId?: string | null;
    fileAssetId?: string | null;
    acceptedByUserId?: string | null;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const status = data.status ?? 'accepted';
  const { error } = await client.from('patient_consents').insert({
    clinic_id: clinicId,
    patient_id: patientId,
    record_id: data.recordId ?? null,
    appointment_id: data.appointmentId ?? null,
    template_id: data.templateId ?? null,
    file_asset_id: data.fileAssetId ?? null,
    title: data.title.trim(),
    version: data.version?.trim() || null,
    status,
    accepted_at: status === 'accepted' ? new Date().toISOString() : null,
    accepted_by_user_id: status === 'accepted' ? data.acceptedByUserId ?? null : null,
  });

  if (error) {
    throw new Error(error.message || 'No se pudo guardar el consentimiento.');
  }
}

export async function createSupabaseClinicalDocument(
  clinicIdInput: string,
  patientId: string,
  data: {
    title: string;
    documentType: 'prescription' | 'exam_order' | 'medical_order' | 'certificate' | 'consent' | 'clinical_summary' | 'quote' | 'invoice' | 'other';
    bodyText?: string;
    status?: 'draft' | 'final' | 'signed' | 'cancelled';
    recordId?: string | null;
    appointmentId?: string | null;
    professionalId?: string | null;
    templateId?: string | null;
    fileAssetId?: string | null;
    createdByUserId?: string | null;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client.from('clinical_documents').insert({
    clinic_id: clinicId,
    patient_id: patientId,
    record_id: data.recordId ?? null,
    appointment_id: data.appointmentId ?? null,
    professional_id: data.professionalId ?? null,
    template_id: data.templateId ?? null,
    file_asset_id: data.fileAssetId ?? null,
    document_type: data.documentType,
    title: data.title.trim(),
    status: data.status ?? 'final',
    body: {
      text: data.bodyText?.trim() || '',
    },
    issued_at: new Date().toISOString(),
    created_by_user_id: data.createdByUserId ?? null,
    updated_by_user_id: data.createdByUserId ?? null,
  });

  if (error) {
    throw new Error(error.message || 'No se pudo guardar el documento clínico.');
  }
}

export async function createSupabaseInternalNote(
  clinicIdInput: string,
  data: {
    patientId?: string | null;
    appointmentId?: string | null;
    professionalId?: string | null;
    authorUserId?: string | null;
    title?: string;
    body: string;
    visibility?: 'clinic' | 'admins' | 'finance' | 'professional_only' | 'support';
    pinned?: boolean;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client.from('internal_notes').insert({
    clinic_id: clinicId,
    patient_id: data.patientId ?? null,
    appointment_id: data.appointmentId ?? null,
    professional_id: data.professionalId ?? null,
    author_user_id: data.authorUserId ?? null,
    title: data.title?.trim() || null,
    body: data.body.trim(),
    visibility: data.visibility ?? 'clinic',
    pinned: data.pinned ?? false,
  });

  if (error) {
    throw new Error(error.message || 'No se pudo guardar la nota interna.');
  }
}

export async function createSupabaseFollowUpTask(
  clinicIdInput: string,
  data: {
    patientId?: string | null;
    appointmentId?: string | null;
    professionalId?: string | null;
    assignedToUserId?: string | null;
    createdByUserId?: string | null;
    title: string;
    description?: string;
    dueAt?: string;
    taskType?: 'follow_up' | 'call' | 'whatsapp' | 'email' | 'document' | 'billing' | 'clinical' | 'other';
    priority?: 'low' | 'medium' | 'high' | 'urgent';
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client.from('follow_up_tasks').insert({
    clinic_id: clinicId,
    patient_id: data.patientId ?? null,
    appointment_id: data.appointmentId ?? null,
    professional_id: data.professionalId ?? null,
    assigned_to_user_id: data.assignedToUserId ?? null,
    created_by_user_id: data.createdByUserId ?? null,
    task_type: data.taskType ?? 'follow_up',
    title: data.title.trim(),
    description: data.description?.trim() || null,
    due_at: data.dueAt ? ensureIsoString(data.dueAt) : null,
    priority: data.priority ?? 'medium',
    status: 'pending',
  });

  if (error) {
    throw new Error(error.message || 'No se pudo guardar la tarea de seguimiento.');
  }
}

export async function loadSupabasePatientWorkspace(clinicIdInput: string, patientId: string) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();

  const [
    fileAssetsResult,
    consentsResult,
    documentsResult,
    internalNotesResult,
    followUpsResult,
    auditLogsResult,
  ] = await Promise.all([
    client
      .from('file_assets')
      .select('id, bucket_id, object_path, original_filename, mime_type, file_size_bytes, category, uploaded_by_user_id, created_at')
      .eq('clinic_id', clinicId)
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false })
      .limit(50),
    client
      .from('patient_consents')
      .select('id, title, version, status, accepted_at, accepted_by_user_id, file_asset_id, created_at')
      .eq('clinic_id', clinicId)
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false })
      .limit(50),
    client
      .from('clinical_documents')
      .select('id, title, document_type, status, body, file_asset_id, created_at, issued_at')
      .eq('clinic_id', clinicId)
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false })
      .limit(50),
    client
      .from('internal_notes')
      .select('id, title, body, visibility, pinned, author_user_id, created_at')
      .eq('clinic_id', clinicId)
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false })
      .limit(50),
    client
      .from('follow_up_tasks')
      .select('id, title, description, status, priority, due_at, completed_at, assigned_to_user_id, created_by_user_id, created_at')
      .eq('clinic_id', clinicId)
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false })
      .limit(50),
    client
      .from('audit_logs')
      .select('id, action, entity_type, source, actor_user_id, created_at')
      .eq('clinic_id', clinicId)
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false })
      .limit(100),
  ]);

  if (fileAssetsResult.error) {
    throw new Error(fileAssetsResult.error.message || 'No se pudieron cargar los adjuntos.');
  }

  if (consentsResult.error) {
    throw new Error(consentsResult.error.message || 'No se pudieron cargar los consentimientos.');
  }

  if (documentsResult.error) {
    throw new Error(documentsResult.error.message || 'No se pudieron cargar los documentos clínicos.');
  }

  if (internalNotesResult.error) {
    throw new Error(internalNotesResult.error.message || 'No se pudieron cargar las notas internas.');
  }

  if (followUpsResult.error) {
    throw new Error(followUpsResult.error.message || 'No se pudieron cargar las tareas de seguimiento.');
  }

  if (auditLogsResult.error) {
    throw new Error(auditLogsResult.error.message || 'No se pudo cargar la auditoría del paciente.');
  }

  const fileAssets = ((fileAssetsResult.data ?? []) as Array<Record<string, unknown>>).map((item) => ({
    id: String(item.id),
    bucketId: String(item.bucket_id ?? 'medflow-clinical-files'),
    objectPath: String(item.object_path ?? ''),
    originalFilename: String(item.original_filename ?? 'Archivo'),
    mimeType: item.mime_type ? String(item.mime_type) : null,
    fileSizeBytes: Number(item.file_size_bytes ?? 0),
    category: String(item.category ?? 'document'),
    uploadedByUserId: item.uploaded_by_user_id ? String(item.uploaded_by_user_id) : null,
    createdAt: String(item.created_at ?? new Date().toISOString()),
  }));

  const consents = ((consentsResult.data ?? []) as Array<Record<string, unknown>>).map((item) => ({
    id: String(item.id),
    title: String(item.title ?? 'Consentimiento'),
    version: item.version ? String(item.version) : null,
    status: String(item.status ?? 'draft'),
    acceptedAt: item.accepted_at ? String(item.accepted_at) : null,
    acceptedByUserId: item.accepted_by_user_id ? String(item.accepted_by_user_id) : null,
    fileAssetId: item.file_asset_id ? String(item.file_asset_id) : null,
    createdAt: String(item.created_at ?? new Date().toISOString()),
  }));

  const documents = ((documentsResult.data ?? []) as Array<Record<string, unknown>>).map((item) => ({
    id: String(item.id),
    title: String(item.title ?? 'Documento clínico'),
    documentType: String(item.document_type ?? 'other'),
    status: String(item.status ?? 'draft'),
    bodyText: String(((item.body as Record<string, unknown> | null)?.text as string | undefined) ?? ''),
    fileAssetId: item.file_asset_id ? String(item.file_asset_id) : null,
    createdAt: String(item.created_at ?? new Date().toISOString()),
    issuedAt: item.issued_at ? String(item.issued_at) : null,
  }));

  const internalNotes = ((internalNotesResult.data ?? []) as Array<Record<string, unknown>>).map((item) => ({
    id: String(item.id),
    title: item.title ? String(item.title) : null,
    body: String(item.body ?? ''),
    visibility: String(item.visibility ?? 'clinic'),
    pinned: Boolean(item.pinned),
    authorUserId: item.author_user_id ? String(item.author_user_id) : null,
    createdAt: String(item.created_at ?? new Date().toISOString()),
  }));

  const followUpTasks = ((followUpsResult.data ?? []) as Array<Record<string, unknown>>).map((item) => ({
    id: String(item.id),
    title: String(item.title ?? 'Seguimiento'),
    description: item.description ? String(item.description) : null,
    status: String(item.status ?? 'pending'),
    priority: String(item.priority ?? 'medium'),
    dueAt: item.due_at ? String(item.due_at) : null,
    completedAt: item.completed_at ? String(item.completed_at) : null,
    assignedToUserId: item.assigned_to_user_id ? String(item.assigned_to_user_id) : null,
    createdByUserId: item.created_by_user_id ? String(item.created_by_user_id) : null,
    createdAt: String(item.created_at ?? new Date().toISOString()),
  }));

  const auditLogs = ((auditLogsResult.data ?? []) as Array<Record<string, unknown>>).map((item) => ({
    id: String(item.id),
    action: String(item.action ?? 'update'),
    entityType: String(item.entity_type ?? 'entity'),
    source: String(item.source ?? 'system'),
    actorUserId: item.actor_user_id ? String(item.actor_user_id) : null,
    createdAt: String(item.created_at ?? new Date().toISOString()),
  }));

  const userProfiles = await fetchUserProfiles(
    unique([
      ...fileAssets.map((item) => item.uploadedByUserId),
      ...consents.map((item) => item.acceptedByUserId),
      ...internalNotes.map((item) => item.authorUserId),
      ...followUpTasks.map((item) => item.assignedToUserId),
      ...followUpTasks.map((item) => item.createdByUserId),
      ...auditLogs.map((item) => item.actorUserId),
    ])
  );

  const fileAssetMap = new Map(fileAssets.map((item) => [item.id, item] as const));

  return {
    files: fileAssets.map((item) => ({
      ...item,
      uploadedByName: item.uploadedByUserId ? userProfiles.get(item.uploadedByUserId)?.fullName ?? 'Usuario' : null,
    })),
    consents: consents.map((item) => ({
      ...item,
      fileName: item.fileAssetId ? fileAssetMap.get(item.fileAssetId)?.originalFilename ?? null : null,
      acceptedByName: item.acceptedByUserId ? userProfiles.get(item.acceptedByUserId)?.fullName ?? 'Usuario' : null,
    })),
    documents: documents.map((item) => ({
      ...item,
      fileName: item.fileAssetId ? fileAssetMap.get(item.fileAssetId)?.originalFilename ?? null : null,
    })),
    internalNotes: internalNotes.map((item) => ({
      ...item,
      authorName: item.authorUserId ? userProfiles.get(item.authorUserId)?.fullName ?? 'Usuario' : null,
    })),
    followUpTasks: followUpTasks.map((item) => ({
      ...item,
      assignedToName: item.assignedToUserId ? userProfiles.get(item.assignedToUserId)?.fullName ?? 'Usuario' : null,
      createdByName: item.createdByUserId ? userProfiles.get(item.createdByUserId)?.fullName ?? 'Usuario' : null,
    })),
    auditLogs: auditLogs.map((item) => ({
      ...item,
      actorName: item.actorUserId ? userProfiles.get(item.actorUserId)?.fullName ?? 'Usuario' : null,
    })),
  };
}

function ensureIsoString(value: string) {
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    throw new Error('La fecha u hora seleccionada no es válida.');
  }

  return parsed.toISOString();
}

export async function createSupabaseAppointment(
  clinicIdInput: string,
  data: {
    patientId: string;
    professionalId: string;
    serviceId: string;
    startTime: string;
    endTime?: string;
    notes?: string;
    boxId?: string | null;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();

  const { data: service, error: serviceError } = await client
    .from('services')
    .select('id, duration_minutes, is_active')
    .eq('clinic_id', clinicId)
    .eq('id', data.serviceId)
    .single();

  if (serviceError || !service || service.is_active === false) {
    throw new Error('El servicio seleccionado no está disponible.');
  }

  const startTime = ensureIsoString(data.startTime);
  const endTime = data.endTime
    ? ensureIsoString(data.endTime)
    : addMinutes(new Date(startTime), Number(service.duration_minutes ?? 0)).toISOString();

  const { data: conflictingAppointments, error: conflictError } = await client
    .from('appointments')
    .select('id')
    .eq('clinic_id', clinicId)
    .eq('professional_id', data.professionalId)
    .not('status', 'in', '(cancelled,no_show)')
    .lte('start_time', endTime)
    .gte('end_time', startTime)
    .limit(1);

  if (conflictError) {
    throw new Error(conflictError.message || 'No se pudo validar la disponibilidad de la cita.');
  }

  if ((conflictingAppointments ?? []).length > 0) {
    throw new Error('El profesional ya tiene una cita en ese horario.');
  }

  const { error } = await client
    .from('appointments')
    .insert({
      clinic_id: clinicId,
      patient_id: data.patientId,
      professional_id: data.professionalId,
      service_id: data.serviceId,
      box_id: data.boxId ?? null,
      start_time: startTime,
      end_time: endTime,
      notes: data.notes?.trim() || null,
      status: 'pending',
      source: 'manual',
    });

  if (error) {
    throw new Error(error.message || 'No se pudo crear la cita.');
  }
}

export async function updateSupabaseAppointment(
  appointmentId: string,
  data: {
    status?: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
    notes?: string;
  }
) {
  const client = getSupabaseClient();
  const payload: Record<string, unknown> = {};

  if (typeof data.status !== 'undefined') {
    payload.status = data.status;
  }

  if (typeof data.notes !== 'undefined') {
    payload.notes = data.notes?.trim() || null;
  }

  const { error } = await client
    .from('appointments')
    .update(payload)
    .eq('id', appointmentId);

  if (error) {
    throw new Error(error.message || 'No se pudo actualizar la cita.');
  }
}

export async function createSupabasePayment(
  clinicIdInput: string,
  data: {
    patientId: string;
    appointmentId?: string;
    professionalId?: string;
    serviceId?: string;
    boxId?: string;
    amount: number;
    paymentMethod: 'cash' | 'card' | 'transfer' | 'other';
    status: 'pending' | 'completed' | 'refunded' | 'partial';
    description?: string;
    paidAt: string;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client
    .from('payments')
    .insert({
      clinic_id: clinicId,
      appointment_id: data.appointmentId ?? null,
      patient_id: data.patientId,
      professional_id: data.professionalId ?? null,
      service_id: data.serviceId ?? null,
      box_id: data.boxId ?? null,
      amount: data.amount,
      payment_method: data.paymentMethod,
      status: data.status,
      description: data.description?.trim() || null,
      paid_at: ensureIsoString(data.paidAt),
    });

  if (error) {
    throw new Error(error.message || 'No se pudo registrar el pago.');
  }
}

export async function updateSupabasePayment(
  paymentId: string,
  data: Partial<{
    amount: number;
    paymentMethod: 'cash' | 'card' | 'transfer' | 'other';
    status: 'pending' | 'completed' | 'refunded' | 'partial';
    description?: string;
  }>
) {
  const client = getSupabaseClient();
  const payload: Record<string, unknown> = {};

  if (typeof data.amount !== 'undefined') {
    payload.amount = data.amount;
  }

  if (typeof data.paymentMethod !== 'undefined') {
    payload.payment_method = data.paymentMethod;
  }

  if (typeof data.status !== 'undefined') {
    payload.status = data.status;
  }

  if (typeof data.description !== 'undefined') {
    payload.description = data.description?.trim() || null;
  }

  const { error } = await client.from('payments').update(payload).eq('id', paymentId);

  if (error) {
    throw new Error(error.message || 'No se pudo actualizar el pago.');
  }
}

export async function deleteSupabasePayment(paymentId: string) {
  const client = getSupabaseClient();
  const { error } = await client.from('payments').delete().eq('id', paymentId);

  if (error) {
    throw new Error(error.message || 'No se pudo eliminar el pago.');
  }
}

export async function loadSupabaseFinance(clinicIdInput: string, params: { startDate: string; endDate: string }) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('payments')
    .select('id, amount, payment_method, status, description, paid_at, patient_id, professional_id, service_id, box_id')
    .eq('clinic_id', clinicId)
    .gte('paid_at', `${params.startDate}T00:00:00.000Z`)
    .lte('paid_at', `${params.endDate}T23:59:59.999Z`)
    .order('paid_at', { ascending: false });

  if (error) {
    throw new Error(error.message || 'No se pudieron cargar los pagos.');
  }

  const payments = ((data ?? []) as Array<Record<string, unknown>>).map((payment) => ({
    id: String(payment.id),
    amount: Number(payment.amount ?? 0),
    paymentMethod: String(payment.payment_method ?? 'other'),
    status: String(payment.status ?? 'pending'),
    description: payment.description ? String(payment.description) : undefined,
    paidAt: String(payment.paid_at),
    patientId: String(payment.patient_id),
    professionalId: payment.professional_id ? String(payment.professional_id) : null,
    serviceId: payment.service_id ? String(payment.service_id) : null,
    boxId: payment.box_id ? String(payment.box_id) : null,
  }));

  const patientIds = unique(payments.map((payment) => payment.patientId));
  const professionalIds = unique(payments.map((payment) => payment.professionalId));
  const serviceIds = unique(payments.map((payment) => payment.serviceId));
  const boxIds = unique(payments.map((payment) => payment.boxId));

  const [patientsResult, professionalsResult, servicesResult, boxesResult] = await Promise.all([
    patientIds.length === 0
      ? Promise.resolve([] as Array<{ id: string; fullName: string }>)
      : client.from('patients').select('id, full_name').in('id', patientIds).then(({ data: patientData, error: patientError }) => {
          if (patientError) {
            throw new Error(patientError.message || 'No se pudieron cargar los pacientes.');
          }

          return ((patientData ?? []) as Array<Record<string, unknown>>).map((patient) => ({
            id: String(patient.id),
            fullName: String(patient.full_name ?? 'Paciente'),
          }));
        }),
    professionalIds.length === 0
      ? Promise.resolve([] as Array<{ id: string; userId: string; specialty: string }>)
      : client.from('professionals').select('id, user_id, specialty').in('id', professionalIds).then(({ data: professionalData, error: professionalError }) => {
          if (professionalError) {
            throw new Error(professionalError.message || 'No se pudieron cargar los profesionales.');
          }

          return ((professionalData ?? []) as Array<Record<string, unknown>>).map((professional) => ({
            id: String(professional.id),
            userId: String(professional.user_id),
            specialty: String(professional.specialty ?? ''),
          }));
        }),
    serviceIds.length === 0
      ? Promise.resolve([] as Array<{ id: string; name: string }>)
      : client.from('services').select('id, name').in('id', serviceIds).then(({ data: serviceData, error: serviceError }) => {
          if (serviceError) {
            throw new Error(serviceError.message || 'No se pudieron cargar los servicios.');
          }

          return ((serviceData ?? []) as Array<Record<string, unknown>>).map((service) => ({
            id: String(service.id),
            name: String(service.name ?? 'Servicio'),
          }));
        }),
    boxIds.length === 0
      ? Promise.resolve([] as Array<{ id: string; name: string }>)
      : client.from('boxes').select('id, name').in('id', boxIds).then(({ data: boxData, error: boxError }) => {
          if (boxError) {
            throw new Error(boxError.message || 'No se pudieron cargar los boxes.');
          }

          return ((boxData ?? []) as Array<Record<string, unknown>>).map((box) => ({
            id: String(box.id),
            name: String(box.name ?? 'Box'),
          }));
        }),
  ]);

  const professionalProfiles = await fetchUserProfiles(unique(professionalsResult.map((professional) => professional.userId)));
  const patientsMap = new Map(patientsResult.map((patient) => [patient.id, patient.fullName]));
  const servicesMap = new Map(servicesResult.map((service) => [service.id, service.name]));
  const boxesMap = new Map(boxesResult.map((box) => [box.id, box.name]));
  const professionalsMap = new Map(
    professionalsResult.map((professional) => [
      professional.id,
      {
        name: professionalProfiles.get(professional.userId)?.fullName ?? 'Profesional',
        specialty: professional.specialty,
      },
    ])
  );

  const summaryByMethod = new Map<string, { total: number; count: number }>();
  const summaryByStatus = new Map<string, { total: number; count: number }>();
  const byService = new Map<string, { serviceId: string; serviceName: string; total: number; count: number }>();
  const byProfessional = new Map<string, { professionalId: string; professionalName: string; specialty: string; total: number; count: number }>();
  const byBox = new Map<string, { boxId: string; boxName: string; total: number; count: number }>();
  const byDay = new Map<string, { total: number; count: number }>();

  for (const payment of payments) {
    const methodBucket = summaryByMethod.get(payment.paymentMethod) ?? { total: 0, count: 0 };
    methodBucket.total += payment.amount;
    methodBucket.count += 1;
    summaryByMethod.set(payment.paymentMethod, methodBucket);

    const statusBucket = summaryByStatus.get(payment.status) ?? { total: 0, count: 0 };
    statusBucket.total += payment.amount;
    statusBucket.count += 1;
    summaryByStatus.set(payment.status, statusBucket);

    if (payment.serviceId) {
      const serviceBucket = byService.get(payment.serviceId) ?? {
        serviceId: payment.serviceId,
        serviceName: servicesMap.get(payment.serviceId) ?? 'Servicio',
        total: 0,
        count: 0,
      };
      serviceBucket.total += payment.amount;
      serviceBucket.count += 1;
      byService.set(payment.serviceId, serviceBucket);
    }

    if (payment.professionalId) {
      const professionalInfo = professionalsMap.get(payment.professionalId);
      const professionalBucket = byProfessional.get(payment.professionalId) ?? {
        professionalId: payment.professionalId,
        professionalName: professionalInfo?.name ?? 'Profesional',
        specialty: professionalInfo?.specialty ?? '',
        total: 0,
        count: 0,
      };
      professionalBucket.total += payment.amount;
      professionalBucket.count += 1;
      byProfessional.set(payment.professionalId, professionalBucket);
    }

    if (payment.boxId) {
      const boxBucket = byBox.get(payment.boxId) ?? {
        boxId: payment.boxId,
        boxName: boxesMap.get(payment.boxId) ?? 'Box',
        total: 0,
        count: 0,
      };
      boxBucket.total += payment.amount;
      boxBucket.count += 1;
      byBox.set(payment.boxId, boxBucket);
    }

    const dayKey = payment.paidAt.slice(0, 10);
    const dayBucket = byDay.get(dayKey) ?? { total: 0, count: 0 };
    dayBucket.total += payment.amount;
    dayBucket.count += 1;
    byDay.set(dayKey, dayBucket);
  }

  const start = parseISO(`${params.startDate}T00:00:00.000Z`);
  const end = parseISO(`${params.endDate}T00:00:00.000Z`);
  const daily = eachDayOfInterval({ start, end }).map((day) => {
    const dayKey = format(day, 'yyyy-MM-dd');
    const bucket = byDay.get(dayKey);

    return {
      date: dayKey,
      total: bucket?.total ?? 0,
      count: bucket?.count ?? 0,
    };
  });

  const totalRevenue = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const totalPayments = payments.length;
  const averageTicket = totalPayments > 0 ? totalRevenue / totalPayments : 0;

  return {
    summary: {
      totalRevenue,
      totalPayments,
      averageTicket,
      byMethod: Array.from(summaryByMethod.entries())
        .map(([method, values]) => ({ method, total: values.total, count: values.count }))
        .sort((left, right) => right.total - left.total),
      byStatus: Array.from(summaryByStatus.entries())
        .map(([status, values]) => ({ status, total: values.total, count: values.count }))
        .sort((left, right) => right.total - left.total),
    },
    byService: Array.from(byService.values())
      .map((service) => ({
        ...service,
        averageTicket: service.count > 0 ? service.total / service.count : 0,
      }))
      .sort((left, right) => right.total - left.total),
    byProfessional: Array.from(byProfessional.values())
      .map((professional) => ({
        ...professional,
        averageTicket: professional.count > 0 ? professional.total / professional.count : 0,
      }))
      .sort((left, right) => right.total - left.total),
    byBox: Array.from(byBox.values()).sort((left, right) => right.total - left.total),
    daily,
    payments: payments.map((payment) => ({
      id: payment.id,
      amount: payment.amount,
      paymentMethod: payment.paymentMethod,
      status: payment.status,
      description: payment.description,
      paidAt: payment.paidAt,
      patient: { name: patientsMap.get(payment.patientId) ?? 'Paciente' },
      professional: payment.professionalId
        ? { user: { name: professionalsMap.get(payment.professionalId)?.name ?? 'Profesional' } }
        : undefined,
      service: payment.serviceId ? { name: servicesMap.get(payment.serviceId) ?? 'Servicio' } : undefined,
      box: payment.boxId ? { name: boxesMap.get(payment.boxId) ?? 'Box' } : undefined,
    })),
  };
}

async function sha256Hex(value: string) {
  if (!globalThis.crypto?.subtle) {
    throw new Error('No se pudo acceder al motor criptográfico del navegador.');
  }

  const encoded = new TextEncoder().encode(value);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function toIsoOrNull(value?: string | null) {
  if (!value) {
    return null;
  }

  return new Date(value).toISOString();
}

export async function loadSupabaseAgentWorkspace(clinicIdInput: string) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();

  const [knowledgeResult, conversationsResult, memoryResult, toolExecutionResult, handoffResult] = await Promise.all([
    client
      .from('agent_knowledge_items')
      .select('id, professional_id, created_by_user_id, title, source_type, source_ref, content, metadata, is_active, created_at, updated_at')
      .eq('clinic_id', clinicId)
      .order('updated_at', { ascending: false }),
    client
      .from('agent_conversations')
      .select('id, professional_id, patient_id, channel, status, summary, sentiment, last_message_at, metadata, created_at, updated_at')
      .eq('clinic_id', clinicId)
      .order('last_message_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false }),
    client
      .from('agent_memory_entries')
      .select('id, conversation_id, professional_id, patient_id, created_by_user_id, memory_type, importance, content, created_at')
      .eq('clinic_id', clinicId)
      .order('created_at', { ascending: false })
      .limit(250),
    client
      .from('agent_tool_executions')
      .select('id, conversation_id, professional_id, tool_name, status, request_payload, response_payload, latency_ms, error_message, created_at')
      .eq('clinic_id', clinicId)
      .order('created_at', { ascending: false })
      .limit(200),
    client
      .from('agent_handoffs')
      .select('id, conversation_id, patient_id, professional_id, requested_by_user_id, assigned_to_user_id, reason, status, resolution_notes, resolved_at, created_at, updated_at')
      .eq('clinic_id', clinicId)
      .order('created_at', { ascending: false }),
  ]);

  if (knowledgeResult.error) {
    throw new Error(knowledgeResult.error.message || 'No se pudo cargar la base de conocimiento.');
  }

  if (conversationsResult.error) {
    throw new Error(conversationsResult.error.message || 'No se pudieron cargar las conversaciones del agente.');
  }

  if (memoryResult.error) {
    throw new Error(memoryResult.error.message || 'No se pudo cargar la memoria del agente.');
  }

  if (toolExecutionResult.error) {
    throw new Error(toolExecutionResult.error.message || 'No se pudieron cargar las ejecuciones de herramientas.');
  }

  if (handoffResult.error) {
    throw new Error(handoffResult.error.message || 'No se pudieron cargar las derivaciones humanas.');
  }

  const knowledgeRows = (knowledgeResult.data ?? []) as Array<Record<string, unknown>>;
  const conversationRows = (conversationsResult.data ?? []) as Array<Record<string, unknown>>;
  const memoryRows = (memoryResult.data ?? []) as Array<Record<string, unknown>>;
  const executionRows = (toolExecutionResult.data ?? []) as Array<Record<string, unknown>>;
  const handoffRows = (handoffResult.data ?? []) as Array<Record<string, unknown>>;

  const professionalIds = unique([
    ...knowledgeRows.map((row) => (row.professional_id ? String(row.professional_id) : null)),
    ...conversationRows.map((row) => (row.professional_id ? String(row.professional_id) : null)),
    ...memoryRows.map((row) => (row.professional_id ? String(row.professional_id) : null)),
    ...executionRows.map((row) => (row.professional_id ? String(row.professional_id) : null)),
    ...handoffRows.map((row) => (row.professional_id ? String(row.professional_id) : null)),
  ]);
  const patientIds = unique([
    ...conversationRows.map((row) => (row.patient_id ? String(row.patient_id) : null)),
    ...memoryRows.map((row) => (row.patient_id ? String(row.patient_id) : null)),
    ...handoffRows.map((row) => (row.patient_id ? String(row.patient_id) : null)),
  ]);
  const userIds = unique([
    ...knowledgeRows.map((row) => (row.created_by_user_id ? String(row.created_by_user_id) : null)),
    ...memoryRows.map((row) => (row.created_by_user_id ? String(row.created_by_user_id) : null)),
    ...handoffRows.map((row) => (row.requested_by_user_id ? String(row.requested_by_user_id) : null)),
    ...handoffRows.map((row) => (row.assigned_to_user_id ? String(row.assigned_to_user_id) : null)),
  ]);

  const [professionalRows, patientRows, userProfiles] = await Promise.all([
    professionalIds.length === 0
      ? Promise.resolve([] as Array<{ id: string; userId: string; specialty: string; color: string; isActive: boolean }>)
      : fetchProfessionalsBase(clinicId, false).then((rows) => rows.filter((row) => professionalIds.includes(row.id))),
    patientIds.length === 0
      ? Promise.resolve([] as Array<{ id: string; fullName: string }>)
      : client.from('patients').select('id, full_name').in('id', patientIds).then(({ data, error }) => {
          if (error) {
            throw new Error(error.message || 'No se pudieron cargar los pacientes del agente.');
          }

          return ((data ?? []) as Array<Record<string, unknown>>).map((patient) => ({
            id: String(patient.id),
            fullName: String(patient.full_name ?? 'Paciente'),
          }));
        }),
    fetchUserProfiles(userIds),
  ]);

  const professionalProfiles = await fetchUserProfiles(unique(professionalRows.map((row) => row.userId)));
  const professionalsMap = new Map(
    professionalRows.map((professional) => [
      professional.id,
      {
        id: professional.id,
        userId: professional.userId,
        name: professionalProfiles.get(professional.userId)?.fullName ?? 'Profesional',
        specialty: professional.specialty,
        color: professional.color,
        isActive: professional.isActive,
      },
    ])
  );
  const patientsMap = new Map(patientRows.map((patient) => [patient.id, patient.fullName]));

  return {
    professionals: Array.from(professionalsMap.values()).sort((left, right) => left.name.localeCompare(right.name, 'es')),
    patients: patientRows.map((patient) => ({ id: patient.id, name: patient.fullName })),
    knowledgeItems: knowledgeRows.map((item) => ({
      id: String(item.id),
      professionalId: item.professional_id ? String(item.professional_id) : null,
      professionalName: item.professional_id ? (professionalsMap.get(String(item.professional_id))?.name ?? 'Profesional') : null,
      createdByUserId: item.created_by_user_id ? String(item.created_by_user_id) : null,
      createdByName: item.created_by_user_id ? (userProfiles.get(String(item.created_by_user_id))?.fullName ?? 'Usuario') : null,
      title: String(item.title ?? 'Documento'),
      sourceType: String(item.source_type ?? 'other'),
      sourceRef: item.source_ref ? String(item.source_ref) : null,
      content: item.content ? String(item.content) : '',
      metadata: (item.metadata as Record<string, unknown> | null) ?? {},
      isActive: Boolean(item.is_active),
      createdAt: String(item.created_at),
      updatedAt: String(item.updated_at),
    })),
    conversations: conversationRows.map((conversation) => {
      const conversationId = String(conversation.id);
      const memoryCount = memoryRows.filter((entry) => String(entry.conversation_id) === conversationId).length;
      const executionCount = executionRows.filter((entry) => entry.conversation_id && String(entry.conversation_id) === conversationId).length;
      return {
        id: conversationId,
        professionalId: conversation.professional_id ? String(conversation.professional_id) : null,
        professionalName: conversation.professional_id
          ? (professionalsMap.get(String(conversation.professional_id))?.name ?? 'Profesional')
          : null,
        patientId: conversation.patient_id ? String(conversation.patient_id) : null,
        patientName: conversation.patient_id ? (patientsMap.get(String(conversation.patient_id)) ?? 'Paciente') : null,
        channel: String(conversation.channel ?? 'whatsapp'),
        status: String(conversation.status ?? 'active'),
        summary: conversation.summary ? String(conversation.summary) : '',
        sentiment: conversation.sentiment ? String(conversation.sentiment) : null,
        lastMessageAt: conversation.last_message_at ? String(conversation.last_message_at) : null,
        metadata: (conversation.metadata as Record<string, unknown> | null) ?? {},
        createdAt: String(conversation.created_at),
        updatedAt: String(conversation.updated_at),
        memoryCount,
        executionCount,
      };
    }),
    memoryEntries: memoryRows.map((entry) => ({
      id: String(entry.id),
      conversationId: String(entry.conversation_id),
      professionalId: entry.professional_id ? String(entry.professional_id) : null,
      professionalName: entry.professional_id ? (professionalsMap.get(String(entry.professional_id))?.name ?? 'Profesional') : null,
      patientId: entry.patient_id ? String(entry.patient_id) : null,
      patientName: entry.patient_id ? (patientsMap.get(String(entry.patient_id)) ?? 'Paciente') : null,
      createdByUserId: entry.created_by_user_id ? String(entry.created_by_user_id) : null,
      createdByName: entry.created_by_user_id ? (userProfiles.get(String(entry.created_by_user_id))?.fullName ?? 'Usuario') : null,
      memoryType: String(entry.memory_type ?? 'context'),
      importance: Number(entry.importance ?? 0),
      content: (entry.content as Record<string, unknown> | null) ?? {},
      createdAt: String(entry.created_at),
    })),
    toolExecutions: executionRows.map((execution) => ({
      id: String(execution.id),
      conversationId: execution.conversation_id ? String(execution.conversation_id) : null,
      professionalId: execution.professional_id ? String(execution.professional_id) : null,
      professionalName: execution.professional_id
        ? (professionalsMap.get(String(execution.professional_id))?.name ?? 'Profesional')
        : null,
      toolName: String(execution.tool_name ?? 'tool'),
      status: String(execution.status ?? 'success'),
      requestPayload: (execution.request_payload as Record<string, unknown> | null) ?? {},
      responsePayload: (execution.response_payload as Record<string, unknown> | null) ?? {},
      latencyMs: execution.latency_ms ? Number(execution.latency_ms) : null,
      errorMessage: execution.error_message ? String(execution.error_message) : null,
      createdAt: String(execution.created_at),
    })),
    handoffs: handoffRows.map((handoff) => ({
      id: String(handoff.id),
      conversationId: handoff.conversation_id ? String(handoff.conversation_id) : null,
      patientId: handoff.patient_id ? String(handoff.patient_id) : null,
      patientName: handoff.patient_id ? (patientsMap.get(String(handoff.patient_id)) ?? 'Paciente') : null,
      professionalId: handoff.professional_id ? String(handoff.professional_id) : null,
      professionalName: handoff.professional_id
        ? (professionalsMap.get(String(handoff.professional_id))?.name ?? 'Profesional')
        : null,
      requestedByUserId: handoff.requested_by_user_id ? String(handoff.requested_by_user_id) : null,
      requestedByName: handoff.requested_by_user_id
        ? (userProfiles.get(String(handoff.requested_by_user_id))?.fullName ?? 'Usuario')
        : null,
      assignedToUserId: handoff.assigned_to_user_id ? String(handoff.assigned_to_user_id) : null,
      assignedToName: handoff.assigned_to_user_id
        ? (userProfiles.get(String(handoff.assigned_to_user_id))?.fullName ?? 'Usuario')
        : null,
      reason: String(handoff.reason ?? ''),
      status: String(handoff.status ?? 'pending'),
      resolutionNotes: handoff.resolution_notes ? String(handoff.resolution_notes) : '',
      resolvedAt: handoff.resolved_at ? String(handoff.resolved_at) : null,
      createdAt: String(handoff.created_at),
      updatedAt: String(handoff.updated_at),
    })),
  };
}

export async function createSupabaseAgentKnowledgeItem(
  clinicIdInput: string,
  data: {
    professionalId?: string;
    title: string;
    sourceType: string;
    sourceRef?: string;
    content?: string;
    metadata?: Record<string, unknown>;
    isActive?: boolean;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client.from('agent_knowledge_items').insert({
    clinic_id: clinicId,
    professional_id: data.professionalId || null,
    title: data.title.trim(),
    source_type: data.sourceType,
    source_ref: data.sourceRef?.trim() || null,
    content: data.content?.trim() || null,
    metadata: data.metadata ?? {},
    is_active: data.isActive ?? true,
  });

  if (error) {
    throw new Error(error.message || 'No se pudo crear el conocimiento del agente.');
  }
}

export async function createSupabaseAgentConversation(
  clinicIdInput: string,
  data: {
    professionalId?: string;
    patientId?: string;
    channel: string;
    status?: string;
    summary?: string;
    sentiment?: string;
    metadata?: Record<string, unknown>;
    lastMessageAt?: string;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client.from('agent_conversations').insert({
    clinic_id: clinicId,
    professional_id: data.professionalId || null,
    patient_id: data.patientId || null,
    channel: data.channel,
    status: data.status ?? 'active',
    summary: data.summary?.trim() || null,
    sentiment: data.sentiment?.trim() || null,
    metadata: data.metadata ?? {},
    last_message_at: toIsoOrNull(data.lastMessageAt) ?? new Date().toISOString(),
  });

  if (error) {
    throw new Error(error.message || 'No se pudo crear la conversación del agente.');
  }
}

export async function createSupabaseAgentMemoryEntry(
  clinicIdInput: string,
  data: {
    conversationId: string;
    professionalId?: string;
    patientId?: string;
    memoryType: string;
    importance: number;
    content: Record<string, unknown>;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client.from('agent_memory_entries').insert({
    clinic_id: clinicId,
    conversation_id: data.conversationId,
    professional_id: data.professionalId || null,
    patient_id: data.patientId || null,
    memory_type: data.memoryType,
    importance: data.importance,
    content: data.content,
  });

  if (error) {
    throw new Error(error.message || 'No se pudo registrar memoria del agente.');
  }
}

export async function createSupabaseAgentToolExecution(
  clinicIdInput: string,
  data: {
    conversationId?: string;
    professionalId?: string;
    toolName: string;
    status: string;
    requestPayload?: Record<string, unknown>;
    responsePayload?: Record<string, unknown>;
    latencyMs?: number;
    errorMessage?: string;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client.from('agent_tool_executions').insert({
    clinic_id: clinicId,
    conversation_id: data.conversationId || null,
    professional_id: data.professionalId || null,
    tool_name: data.toolName.trim(),
    status: data.status,
    request_payload: data.requestPayload ?? {},
    response_payload: data.responsePayload ?? {},
    latency_ms: typeof data.latencyMs === 'number' ? data.latencyMs : null,
    error_message: data.errorMessage?.trim() || null,
  });

  if (error) {
    throw new Error(error.message || 'No se pudo registrar la ejecución de herramienta.');
  }
}

export async function createSupabaseAgentHandoff(
  clinicIdInput: string,
  data: {
    conversationId?: string;
    patientId?: string;
    professionalId?: string;
    reason: string;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client.from('agent_handoffs').insert({
    clinic_id: clinicId,
    conversation_id: data.conversationId || null,
    patient_id: data.patientId || null,
    professional_id: data.professionalId || null,
    reason: data.reason.trim(),
    status: 'pending',
  });

  if (error) {
    throw new Error(error.message || 'No se pudo crear la derivación humana.');
  }
}

export async function updateSupabaseAgentHandoff(
  handoffId: string,
  data: { status?: string; resolutionNotes?: string; assignedToUserId?: string | null }
) {
  const client = getSupabaseClient();
  const payload: Record<string, unknown> = {};

  if (typeof data.status !== 'undefined') {
    payload.status = data.status;
    payload.resolved_at = data.status === 'resolved' ? new Date().toISOString() : null;
  }

  if (typeof data.resolutionNotes !== 'undefined') {
    payload.resolution_notes = data.resolutionNotes.trim() || null;
  }

  if (typeof data.assignedToUserId !== 'undefined') {
    payload.assigned_to_user_id = data.assignedToUserId || null;
  }

  const { error } = await client.from('agent_handoffs').update(payload).eq('id', handoffId);

  if (error) {
    throw new Error(error.message || 'No se pudo actualizar la derivación humana.');
  }
}

export async function loadSupabasePortalWorkspace(clinicIdInput: string) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();

  const [branchResult, planResult, subscriptionResult, tokenResult, messageResult] = await Promise.all([
    client
      .from('clinic_branches')
      .select('id, name, code, address, phone, email, timezone, is_active, metadata, created_at, updated_at')
      .eq('clinic_id', clinicId)
      .order('name', { ascending: true }),
    client
      .from('subscription_plans')
      .select('id, code, name, description, price_monthly, price_yearly, limits, features, is_active')
      .order('price_monthly', { ascending: true }),
    client
      .from('clinic_subscriptions')
      .select('id, plan_id, status, billing_name, billing_email, current_period_start, current_period_end, trial_ends_at, cancel_at_period_end, limits_snapshot, metadata, created_at, updated_at')
      .eq('clinic_id', clinicId)
      .maybeSingle(),
    client
      .from('portal_access_tokens')
      .select('id, branch_id, patient_id, professional_id, created_by_user_id, token_type, status, expires_at, last_used_at, metadata, created_at, updated_at')
      .eq('clinic_id', clinicId)
      .order('created_at', { ascending: false }),
    client
      .from('portal_messages')
      .select('id, token_id, branch_id, patient_id, professional_id, channel, direction, subject, body, read_at, metadata, created_at')
      .eq('clinic_id', clinicId)
      .order('created_at', { ascending: false })
      .limit(200),
  ]);

  if (branchResult.error) {
    throw new Error(branchResult.error.message || 'No se pudieron cargar las sucursales.');
  }

  if (planResult.error) {
    throw new Error(planResult.error.message || 'No se pudieron cargar los planes.');
  }

  if (subscriptionResult.error) {
    throw new Error(subscriptionResult.error.message || 'No se pudo cargar la suscripción.');
  }

  if (tokenResult.error) {
    throw new Error(tokenResult.error.message || 'No se pudieron cargar los accesos de portal.');
  }

  if (messageResult.error) {
    throw new Error(messageResult.error.message || 'No se pudieron cargar los mensajes del portal.');
  }

  const tokenRows = (tokenResult.data ?? []) as Array<Record<string, unknown>>;
  const messageRows = (messageResult.data ?? []) as Array<Record<string, unknown>>;
  const branchRows = (branchResult.data ?? []) as Array<Record<string, unknown>>;
  const professionalIds = unique([
    ...tokenRows.map((row) => (row.professional_id ? String(row.professional_id) : null)),
    ...messageRows.map((row) => (row.professional_id ? String(row.professional_id) : null)),
  ]);
  const patientIds = unique([
    ...tokenRows.map((row) => (row.patient_id ? String(row.patient_id) : null)),
    ...messageRows.map((row) => (row.patient_id ? String(row.patient_id) : null)),
  ]);

  const [professionalRows, patientRows] = await Promise.all([
    professionalIds.length === 0
      ? Promise.resolve([] as Array<{ id: string; userId: string; specialty: string; color: string; isActive: boolean }>)
      : fetchProfessionalsBase(clinicId, false).then((rows) => rows.filter((row) => professionalIds.includes(row.id))),
    patientIds.length === 0
      ? Promise.resolve([] as Array<{ id: string; fullName: string }>)
      : client.from('patients').select('id, full_name').in('id', patientIds).then(({ data, error }) => {
          if (error) {
            throw new Error(error.message || 'No se pudieron cargar los pacientes del portal.');
          }

          return ((data ?? []) as Array<Record<string, unknown>>).map((patient) => ({
            id: String(patient.id),
            fullName: String(patient.full_name ?? 'Paciente'),
          }));
        }),
  ]);

  const professionalProfiles = await fetchUserProfiles(unique(professionalRows.map((row) => row.userId)));
  const professionals = professionalRows.map((professional) => ({
    id: professional.id,
    name: professionalProfiles.get(professional.userId)?.fullName ?? 'Profesional',
    specialty: professional.specialty,
    color: professional.color,
    isActive: professional.isActive,
  }));
  const professionalMap = new Map(professionals.map((professional) => [professional.id, professional]));
  const patientMap = new Map(patientRows.map((patient) => [patient.id, patient.fullName]));
  const branchMap = new Map(branchRows.map((branch) => [String(branch.id), String(branch.name ?? 'Sucursal')]));
  const plans = ((planResult.data ?? []) as Array<Record<string, unknown>>).map((plan) => ({
    id: String(plan.id),
    code: String(plan.code),
    name: String(plan.name),
    description: plan.description ? String(plan.description) : '',
    priceMonthly: Number(plan.price_monthly ?? 0),
    priceYearly: Number(plan.price_yearly ?? 0),
    limits: (plan.limits as Record<string, unknown> | null) ?? {},
    features: Array.isArray(plan.features) ? (plan.features as string[]) : [],
    isActive: Boolean(plan.is_active),
  }));
  const currentSubscriptionRow = subscriptionResult.data as Record<string, unknown> | null;

  return {
    branches: branchRows.map((branch) => ({
      id: String(branch.id),
      name: String(branch.name ?? 'Sucursal'),
      code: branch.code ? String(branch.code) : '',
      address: branch.address ? String(branch.address) : '',
      phone: branch.phone ? String(branch.phone) : '',
      email: branch.email ? String(branch.email) : '',
      timezone: String(branch.timezone ?? 'America/Santiago'),
      isActive: Boolean(branch.is_active),
      metadata: (branch.metadata as Record<string, unknown> | null) ?? {},
      createdAt: String(branch.created_at),
      updatedAt: String(branch.updated_at),
    })),
    patients: patientRows.map((patient) => ({ id: patient.id, name: patient.fullName })),
    professionals,
    subscriptionPlans: plans,
    currentSubscription: currentSubscriptionRow
      ? {
          id: String(currentSubscriptionRow.id),
          planId: currentSubscriptionRow.plan_id ? String(currentSubscriptionRow.plan_id) : null,
          planName: currentSubscriptionRow.plan_id
            ? (plans.find((plan) => plan.id === String(currentSubscriptionRow.plan_id))?.name ?? 'Plan')
            : null,
          status: String(currentSubscriptionRow.status ?? 'trialing'),
          billingName: currentSubscriptionRow.billing_name ? String(currentSubscriptionRow.billing_name) : '',
          billingEmail: currentSubscriptionRow.billing_email ? String(currentSubscriptionRow.billing_email) : '',
          currentPeriodStart: currentSubscriptionRow.current_period_start ? String(currentSubscriptionRow.current_period_start) : null,
          currentPeriodEnd: currentSubscriptionRow.current_period_end ? String(currentSubscriptionRow.current_period_end) : null,
          trialEndsAt: currentSubscriptionRow.trial_ends_at ? String(currentSubscriptionRow.trial_ends_at) : null,
          cancelAtPeriodEnd: Boolean(currentSubscriptionRow.cancel_at_period_end),
          limitsSnapshot: (currentSubscriptionRow.limits_snapshot as Record<string, unknown> | null) ?? {},
          metadata: (currentSubscriptionRow.metadata as Record<string, unknown> | null) ?? {},
          createdAt: String(currentSubscriptionRow.created_at),
          updatedAt: String(currentSubscriptionRow.updated_at),
        }
      : null,
    portalAccessTokens: tokenRows.map((token) => ({
      id: String(token.id),
      branchId: token.branch_id ? String(token.branch_id) : null,
      branchName: token.branch_id ? (branchMap.get(String(token.branch_id)) ?? 'Sucursal') : null,
      patientId: token.patient_id ? String(token.patient_id) : null,
      patientName: token.patient_id ? (patientMap.get(String(token.patient_id)) ?? 'Paciente') : null,
      professionalId: token.professional_id ? String(token.professional_id) : null,
      professionalName: token.professional_id
        ? (professionalMap.get(String(token.professional_id))?.name ?? 'Profesional')
        : null,
      tokenType: String(token.token_type ?? 'patient'),
      status: String(token.status ?? 'active'),
      expiresAt: token.expires_at ? String(token.expires_at) : null,
      lastUsedAt: token.last_used_at ? String(token.last_used_at) : null,
      metadata: (token.metadata as Record<string, unknown> | null) ?? {},
      createdAt: String(token.created_at),
      updatedAt: String(token.updated_at),
    })),
    portalMessages: messageRows.map((message) => ({
      id: String(message.id),
      tokenId: message.token_id ? String(message.token_id) : null,
      branchId: message.branch_id ? String(message.branch_id) : null,
      branchName: message.branch_id ? (branchMap.get(String(message.branch_id)) ?? 'Sucursal') : null,
      patientId: message.patient_id ? String(message.patient_id) : null,
      patientName: message.patient_id ? (patientMap.get(String(message.patient_id)) ?? 'Paciente') : null,
      professionalId: message.professional_id ? String(message.professional_id) : null,
      professionalName: message.professional_id
        ? (professionalMap.get(String(message.professional_id))?.name ?? 'Profesional')
        : null,
      channel: String(message.channel ?? 'portal'),
      direction: String(message.direction ?? 'system'),
      subject: message.subject ? String(message.subject) : '',
      body: String(message.body ?? ''),
      readAt: message.read_at ? String(message.read_at) : null,
      metadata: (message.metadata as Record<string, unknown> | null) ?? {},
      createdAt: String(message.created_at),
    })),
  };
}

export async function createSupabaseClinicBranch(
  clinicIdInput: string,
  data: {
    name: string;
    code?: string;
    address?: string;
    phone?: string;
    email?: string;
    timezone?: string;
    metadata?: Record<string, unknown>;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client.from('clinic_branches').insert({
    clinic_id: clinicId,
    name: data.name.trim(),
    code: data.code?.trim() || null,
    address: data.address?.trim() || null,
    phone: data.phone?.trim() || null,
    email: data.email?.trim() || null,
    timezone: data.timezone?.trim() || 'America/Santiago',
    metadata: data.metadata ?? {},
  });

  if (error) {
    throw new Error(error.message || 'No se pudo crear la sucursal.');
  }
}

export async function updateSupabaseClinicBranch(
  branchId: string,
  data: {
    name?: string;
    code?: string;
    address?: string;
    phone?: string;
    email?: string;
    timezone?: string;
    isActive?: boolean;
    metadata?: Record<string, unknown>;
  }
) {
  const client = getSupabaseClient();
  const payload: Record<string, unknown> = {};

  if (typeof data.name !== 'undefined') payload.name = data.name.trim();
  if (typeof data.code !== 'undefined') payload.code = data.code.trim() || null;
  if (typeof data.address !== 'undefined') payload.address = data.address.trim() || null;
  if (typeof data.phone !== 'undefined') payload.phone = data.phone.trim() || null;
  if (typeof data.email !== 'undefined') payload.email = data.email.trim() || null;
  if (typeof data.timezone !== 'undefined') payload.timezone = data.timezone.trim() || 'America/Santiago';
  if (typeof data.isActive !== 'undefined') payload.is_active = data.isActive;
  if (typeof data.metadata !== 'undefined') payload.metadata = data.metadata;

  const { error } = await client.from('clinic_branches').update(payload).eq('id', branchId);

  if (error) {
    throw new Error(error.message || 'No se pudo actualizar la sucursal.');
  }
}

export async function upsertSupabaseClinicSubscription(
  clinicIdInput: string,
  data: {
    planId?: string;
    status: string;
    billingName?: string;
    billingEmail?: string;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    trialEndsAt?: string;
    cancelAtPeriodEnd?: boolean;
    limitsSnapshot?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client.from('clinic_subscriptions').upsert({
    clinic_id: clinicId,
    plan_id: data.planId || null,
    status: data.status,
    billing_name: data.billingName?.trim() || null,
    billing_email: data.billingEmail?.trim() || null,
    current_period_start: toIsoOrNull(data.currentPeriodStart),
    current_period_end: toIsoOrNull(data.currentPeriodEnd),
    trial_ends_at: toIsoOrNull(data.trialEndsAt),
    cancel_at_period_end: data.cancelAtPeriodEnd ?? false,
    limits_snapshot: data.limitsSnapshot ?? {},
    metadata: data.metadata ?? {},
  }, { onConflict: 'clinic_id' });

  if (error) {
    throw new Error(error.message || 'No se pudo guardar la suscripción de la clínica.');
  }
}

export async function createSupabasePortalAccessToken(
  clinicIdInput: string,
  data: {
    branchId?: string;
    patientId?: string;
    professionalId?: string;
    tokenType: 'patient' | 'professional' | 'admin';
    expiresAt?: string;
    metadata?: Record<string, unknown>;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const plainToken = `medflow_portal_${globalThis.crypto.randomUUID().replace(/-/g, '')}`;
  const tokenHash = await sha256Hex(plainToken);
  const preview = plainToken.slice(-8);
  const { error } = await client.from('portal_access_tokens').insert({
    clinic_id: clinicId,
    branch_id: data.branchId || null,
    patient_id: data.patientId || null,
    professional_id: data.professionalId || null,
    token_hash: tokenHash,
    token_type: data.tokenType,
    status: 'active',
    expires_at: toIsoOrNull(data.expiresAt),
    metadata: {
      ...(data.metadata ?? {}),
      preview,
    },
  });

  if (error) {
    throw new Error(error.message || 'No se pudo crear el acceso de portal.');
  }

  return { token: plainToken, preview };
}

export async function updateSupabasePortalAccessToken(
  tokenId: string,
  data: { status?: string; expiresAt?: string | null; lastUsedAt?: string | null; metadata?: Record<string, unknown> }
) {
  const client = getSupabaseClient();
  const payload: Record<string, unknown> = {};

  if (typeof data.status !== 'undefined') payload.status = data.status;
  if (typeof data.expiresAt !== 'undefined') payload.expires_at = toIsoOrNull(data.expiresAt);
  if (typeof data.lastUsedAt !== 'undefined') payload.last_used_at = toIsoOrNull(data.lastUsedAt);
  if (typeof data.metadata !== 'undefined') payload.metadata = data.metadata;

  const { error } = await client.from('portal_access_tokens').update(payload).eq('id', tokenId);

  if (error) {
    throw new Error(error.message || 'No se pudo actualizar el acceso de portal.');
  }
}

export async function createSupabasePortalMessage(
  clinicIdInput: string,
  data: {
    tokenId?: string;
    branchId?: string;
    patientId?: string;
    professionalId?: string;
    channel: string;
    direction: string;
    subject?: string;
    body: string;
    metadata?: Record<string, unknown>;
  }
) {
  const clinicId = requireClinicId(clinicIdInput);
  const client = getSupabaseClient();
  const { error } = await client.from('portal_messages').insert({
    clinic_id: clinicId,
    token_id: data.tokenId || null,
    branch_id: data.branchId || null,
    patient_id: data.patientId || null,
    professional_id: data.professionalId || null,
    channel: data.channel,
    direction: data.direction,
    subject: data.subject?.trim() || null,
    body: data.body.trim(),
    metadata: data.metadata ?? {},
  });

  if (error) {
    throw new Error(error.message || 'No se pudo enviar el mensaje del portal.');
  }
}
